import React from 'react';
import { Link } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import { useContent } from '../content/ContentContext';
import { getIcon } from '../content/icons';

const defaultProductHero = {
  eyebrow: '产品与课程体系',
  title: '从小球平衡到机器人产品矩阵',
  desc: '神秘电波与 Matrix Cube 点阵魔方领衔，小球平衡、机械臂与灵巧手依次进阶，形成完整的科创产品线。',
};

const defaultCourseProducts = [
  {
    id: 'ball-suite', icon: 'CircleDot', image: '/images/products/infrared-ball-balance.png', name: '小球平衡综合套件', level: '综合', age: '机构课程包',
    color: '#e8935a', tagline: '神秘电波、电子画画与小球平衡的同一套课程线', badge: '核心产品',
    detail: '神秘电波和电子画画都归入小球平衡综合套件：先用触摸输入理解信号与坐标，再过渡到红外遥控控制、传感器反馈和板球平衡的闭环调节。',
    learn: ['神秘电波', '电子画画', '红外遥控', '闭环控制'],
    stages: {
      primary: '从触摸输入、点划信号和屏幕画画开始，建立输入与反馈的直观经验。',
      middle: '加入红外遥控与坐标映射，理解指令、变量变化和控制结果之间的关系。',
      high: '进入小球平衡任务，学习反馈控制、参数调节和系统稳定性。',
    },
  },
  {
    id: 'matrix-cube', icon: 'Cpu', image: '/images/products/matrix-cube.png', name: 'Matrix Cube 编程点阵魔方', level: '入门', age: '机构课程包',
    color: '#0f5fd8', tagline: '点亮创意，连接未来', badge: '课程套件',
    detail: 'Matrix Cube 是面向图形化编程和点阵显示的课程套件，供学校及机构用于作品展示、社团活动和科创项目课。',
    learn: ['图形化编程', '点阵显示', '创意交互', '模块化扩展'],
    stages: {
      primary: '用图形化积木控制点阵显示，完成表情、图案和简单动画。',
      middle: '加入传感器输入和条件逻辑，理解输入、处理、输出的完整链路。',
      high: '扩展通信与模块组合，形成可展示的互动装置。',
    },
  },
  {
    id: 'robotic-arm', icon: 'Workflow', image: '/images/products/robotic-arm.png', name: '桌面机械臂', level: '进阶', age: '机构课程包',
    color: '#12213f', tagline: '把机械结构和运动控制放到桌面上', badge: '机器人产品',
    detail: '桌面机械臂用于机械结构、舵机控制和空间运动理解，可作为机构进阶机器人课程与项目展示套件。',
    learn: ['机械结构', '舵机控制', '运动规划', '任务编排'],
    stages: {
      primary: '观察机械臂关节和夹爪动作，理解角度与位置变化。',
      middle: '编写多关节动作序列，完成抓取、移动和复位任务。',
      high: '结合传感器和路径规划，完成自动化项目挑战。',
    },
  },
  {
    id: 'dexterous-hand', icon: 'Hand', image: '/images/products/dexterous-hand.png', name: '仿生灵巧手', level: '高阶', age: '机构课程包',
    color: '#6b7280', tagline: '用多自由度结构理解仿生控制', badge: '机器人进阶',
    detail: '仿生灵巧手面向高阶机器人课程，通过多关节手指、牵引结构和动作组合，展示抓握、姿态变化和复杂执行器控制。',
    learn: ['仿生结构', '多自由度控制', '动作组合', '机器人执行器'],
    stages: {
      primary: '观察手指关节与牵引结构，理解仿生机械的基本形态。',
      middle: '控制多个舵机完成弯曲、张开和组合手势。',
      high: '设计抓握策略和动作序列，衔接机器人执行器课程。',
    },
  },
];
// 课程封面：蓝色渐变 + 无线电波 + 点划线 + 代码符号 + 光效
function CourseCover({ course }) {
  const Icon = getIcon(course.icon);
  const c1 = course.color || '#2563eb';
  return (
    <div className="relative aspect-[4/3] overflow-hidden rounded-3xl"
      style={{ backgroundImage: `radial-gradient(420px 280px at 70% 15%, rgba(255,255,255,0.22), transparent 60%), linear-gradient(150deg, ${c1}, #0f2a6b)` }}>
      {/* 网格 */}
      <div className="absolute inset-0 tech-grid opacity-25" />
      {/* 漂浮代码符号 */}
      <span className="absolute left-6 top-6 font-mono text-white/30 text-lg">{'{ }'}</span>
      <span className="absolute right-8 top-12 font-mono text-white/25 text-2xl">&lt;/&gt;</span>
      <span className="absolute left-10 bottom-10 font-mono text-white/25 text-base">0 1 0 1</span>
      {/* 中心图标 + 无线电波 */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative flex h-24 w-24 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-sm animate-floaty">
          <Icon className="h-12 w-12 text-white" strokeWidth={1.5} />
          <span className="wave-ring" />
          <span className="wave-ring" style={{ animationDelay: '1.3s' }} />
        </div>
      </div>
      {/* 点划线条 */}
      <div className="absolute inset-x-6 bottom-6 flex items-center justify-center gap-2">
        {['·','—','·','·','—','·','—','—','·','—','·'].map((s, i) => (
          <span key={i} className="text-white/70 text-lg">{s}</span>
        ))}
      </div>
    </div>
  );
}

function Roadmap({ stages, color }) {
  const rows = [['小学', stages.primary], ['中学', stages.middle], ['高中', stages.high]].filter((r) => r[1]);
  if (!rows.length) return null;
  return (
    <div className="mt-6">
      <div className="text-[13px] font-bold mb-3" style={{ color: 'var(--ink)' }}>学段路线</div>
      <div className="relative pl-5">
        <span className="absolute left-[5px] top-1 bottom-1 w-px" style={{ background: 'var(--line)' }} />
        <div className="space-y-4">
          {rows.map(([label, text]) => (
            <div key={label} className="relative">
              <span className="absolute -left-5 top-1.5 h-2.5 w-2.5 rounded-full" style={{ background: color }} />
              <div className="text-[14px] font-semibold" style={{ color: 'var(--ink)' }}>{label}</div>
              <div className="text-[14px] leading-relaxed" style={{ color: 'var(--body)' }}>{text}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Courses() {
  const { courses } = useContent();
  const hero = courses.productHero || defaultProductHero;
  const items = courses.productItems?.length ? courses.productItems : defaultCourseProducts;

  return (
    <div>
      {/* 页头 */}
      <section className="hero-bg relative overflow-hidden">
        <div className="absolute inset-0 tech-grid opacity-50" />
        <div className="container-x relative page-top pb-14 text-center">
          <div className="eyebrow mb-4 reveal">{hero.eyebrow}</div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight reveal" style={{ color: 'var(--ink)', transitionDelay: '0.05s' }}>{hero.title}</h1>
          <p className="mx-auto mt-5 max-w-2xl text-[17px] leading-relaxed reveal" style={{ color: 'var(--body)', transitionDelay: '0.1s' }}>{hero.desc}</p>
        </div>
      </section>

      {/* 课程列表（通栏分段叙事） */}
      <div>
        {items.map((c, i) => {
          const flip = i % 2 === 1;
          return (
            <section key={c.id || i} className={`${flip ? 'section-alt ' : ''}py-16 md:py-24`}>
              <div className="container-x reveal grid items-center gap-10 md:gap-16 md:grid-cols-2">
                <div className={flip ? 'md:order-2' : ''}>
                  {c.image
                    ? <img src={c.image} alt={c.name} className="aspect-[4/3] w-full object-contain bg-white shadow-xl" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                    : <CourseCover course={c} />}
                </div>
                <div className={flip ? 'md:order-1' : ''}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="chip">{c.badge}</span>
                    <span className="text-[13px] font-medium" style={{ color: 'var(--muted)' }}>{c.level} · {c.age}</span>
                  </div>
                  <h2 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight" style={{ color: 'var(--ink)' }}>{c.name}</h2>
                  <p className="mt-2 text-[18px] font-semibold" style={{ color: 'var(--blue-600)' }}>{c.tagline}</p>
                  <p className="mt-4 text-[16px] leading-relaxed" style={{ color: 'var(--body)' }}>{c.detail}</p>

                  {(c.learn || []).length > 0 && (
                    <div className="mt-6">
                      <div className="text-[13px] font-bold mb-2.5" style={{ color: 'var(--ink)' }}>学习收获</div>
                      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                        {c.learn.map((l) => (
                          <div key={l} className="flex items-center gap-2 rounded-xl px-3 py-2 text-[14px]" style={{ background: flip ? '#fff' : 'var(--bg-alt)', color: 'var(--ink)' }}>
                            <Check className="h-4 w-4 shrink-0" style={{ color: c.color }} /> {l}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {c.stages && <Roadmap stages={c.stages} color={c.color || '#2563eb'} />}

                  <Link to="/contact" className="btn btn-blue mt-7">获取机构方案 <ArrowRight className="h-4 w-4" /></Link>
                </div>
              </div>
            </section>
          );
        })}
      </div>

      {/* 底部 CTA */}
      <section className="section-alt py-16 md:py-20 text-center">
        <div className="container-x reveal">
          <h2 className="text-2xl md:text-3xl font-extrabold tracking-tight" style={{ color: 'var(--ink)' }}>需要机构课程包方案？</h2>
          <p className="mt-3 text-[16px]" style={{ color: 'var(--body)' }}>留下合作场景，我们提供课程包、硬件配置与落地建议。</p>
          <Link to="/contact" className="btn btn-blue mt-7">获取合作方案</Link>
        </div>
      </section>
    </div>
  );
}